import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { handleGoogleLogin } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

export default function AuthFinish() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    finishAuth();
  }, []);

  const finishAuth = async () => {
    try {
      // Check if user came from amahly.com and needs to be redirected back
      const urlParams = new URLSearchParams(location.search);
      const referrerDomain = urlParams.get('referrer') || document.referrer;
      const shouldRedirectToAmahly = referrerDomain.includes('amahly.com');
      
      console.log('Checking for existing session...');
      console.log('Current URL:', window.location.href);
      console.log('Referrer domain:', referrerDomain);
      console.log('Should redirect to amahly:', shouldRedirectToAmahly);
      
      // First, check if we already have a session from the OAuth redirect
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw sessionError;
      }
      
      if (!sessionData.session) {
        // Check both URL hash and search params for OAuth tokens
        let accessToken: string | null = null;
        let refreshToken: string | null = null;
        
        // Try URL hash first (standard OAuth redirect)
        if (window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          accessToken = hashParams.get('access_token');
          refreshToken = hashParams.get('refresh_token');
          console.log('Checking URL hash for tokens:', { accessToken: !!accessToken, refreshToken: !!refreshToken });
        }
        
        // Try search params as fallback
        if (!accessToken) {
          const searchParams = new URLSearchParams(window.location.search);
          accessToken = searchParams.get('access_token');
          refreshToken = searchParams.get('refresh_token');
          console.log('Checking search params for tokens:', { accessToken: !!accessToken, refreshToken: !!refreshToken });
        }
        
        if (accessToken) {
          console.log('Found tokens, setting session...');
          
          const { data: setSessionData, error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });
          
          if (setSessionError) {
            console.error('Set session error:', setSessionError);
            throw setSessionError;
          }
          
          if (!setSessionData.session) {
            throw new Error('Failed to establish session from tokens');
          }
          
          console.log('Session established successfully');
        } else {
          console.log('No tokens found in URL');
          throw new Error('No session or authentication tokens found. Please try logging in again.');
        }
      } else {
        console.log('Existing session found');
      }
      
      console.log('Session established, processing user data...');
      
      // Process the user data using existing handleGoogleLogin function
      const user = await handleGoogleLogin();
      
      if (!user) {
        throw new Error('Failed to process user data');
      }
      
      console.log('User data processed successfully:', user.email);
      
      setSuccess(true);
      toast.success('Login successful!');
      
      // Handle cross-domain redirect for amahly.com users
      if (shouldRedirectToAmahly) {
        // Store user data in localStorage for cross-domain access
        const userData = JSON.stringify(user);
        
        // Create a temporary token for secure cross-domain transfer
        const transferToken = btoa(JSON.stringify({
          userData,
          timestamp: Date.now(),
          domain: 'learn.amah-llc.com'
        }));
        
        // Redirect back to amahly.com with the transfer token
        const redirectUrl = `https://amahly.com/auth/complete?token=${encodeURIComponent(transferToken)}`;
        console.log('Redirecting to amahly.com with token:', redirectUrl);
        
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1500);
      } else {
        // Normal redirect for learn.amah-llc.com users
        let redirectPath = '/dashboard';
        const state = urlParams.get('state');
        if (state) {
          try {
            const stateParams = new URLSearchParams(state);
            const redirect = stateParams.get('redirect');
            if (redirect && redirect.startsWith('/')) {
              redirectPath = redirect;
            }
          } catch (e) {
            console.warn('Failed to parse state parameter:', e);
          }
        }
        
        console.log('Redirecting to:', redirectPath);
        
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 1500);
      }
      
    } catch (error: any) {
      console.error('Auth finish error:', error);
      setError(error.message || 'Authentication failed');
      toast.error(error.message || 'Authentication failed');
      
      // Redirect to login after error
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 border-b border-white/10">
          <div className="text-center">
            <img 
              src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/amalieaispanishlogo.png"
              alt="Amalie AI"
              className="w-16 h-16 mx-auto mb-4 rounded-full"
            />
            <h2 className="text-2xl font-bold text-white">Finishing Login</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {loading && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto">
                <FaSpinner className="w-8 h-8 text-yellow-400 animate-spin" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Processing Authentication</h3>
                <p className="text-white/70 text-sm">Exchanging authorization code for session...</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-400">
                  This may take a few seconds. Do not close this window.
                </p>
              </div>
            </div>
          )}

          {success && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <FaCheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Login Successful!</h3>
                <p className="text-white/70 text-sm">
                  {location.search.includes('referrer') && document.referrer.includes('amahly.com') 
                    ? 'Redirecting back to amahly.com...' 
                    : 'Redirecting to your dashboard...'
                  }
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <FaExclamationTriangle className="w-8 h-8 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Authentication Failed</h3>
                <p className="text-red-200 text-sm mb-4">{error}</p>
                <p className="text-white/70 text-xs">Redirecting to login page...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}